import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { loadWorldFeatures, featureIdToCountryNumeric, type CountryFeatureCollection } from "../lib/geo";
import { colorForClicks } from "../lib/colorScale";
import { formatClicks } from "../lib/format";
import type { ClickCounterRow, Country } from "../lib/types";
import { countryDisplayName } from "../hooks/useCountries";
import { useLanguage } from "../i18n/LanguageContext";

// Natural Earth assigns id "-99" to a handful of territories with no
// official ISO 3166-1 numeric code (Kosovo, Somaliland, Northern Cyprus, ...).
// Rendered muted and non-interactive rather than silently hidden - see
// src/lib/geo.ts for the full explanation.
const UNMATCHED_FEATURE_COLOR = "#DDE3EA";
const UNMATCHED_STROKE = "#C7CFD9";

const BORDER_COLOR = "#7C8798";
const HOVER_STROKE = "#0f172a";
const SELECTED_STROKE = "#0f172a";

const DEFAULT_POSITION = { coordinates: [12, 15] as [number, number], zoom: 1 };
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface HoverInfo {
  name: string;
  clicks: number;
  x: number;
  y: number;
}

export interface WorldMapProps {
  countries: Map<number, Country>;
  clicksByCountry: Map<number, ClickCounterRow>;
  maxClicks: number;
  selectedCountryNumeric: number | null;
  onSelectCountry: (countryNumeric: number, countryName: string) => void;
}

export function WorldMap({
  countries,
  clicksByCountry,
  maxClicks,
  selectedCountryNumeric,
  onSelectCountry,
}: WorldMapProps) {
  const { t, language } = useLanguage();
  const [features, setFeatures] = useState<CountryFeatureCollection | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [hover, setHover] = useState<HoverInfo | null>(null);

  useEffect(() => {
    let active = true;
    loadWorldFeatures()
      .then((fc) => {
        if (active) setFeatures(fc);
      })
      .catch((e) => {
        console.error("failed to load world map", e);
        if (active) setLoadError(t("worldmap.loadError"));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetZoom = useCallback(() => setPosition(DEFAULT_POSITION), []);

  const selectedInfo = useMemo(() => {
    if (selectedCountryNumeric === null) return null;
    const country = countries.get(selectedCountryNumeric);
    const clicks = clicksByCountry.get(selectedCountryNumeric)?.clicks ?? 0;
    if (!country) return null;
    return { name: countryDisplayName(country, country.name_en), clicks };
  }, [selectedCountryNumeric, countries, clicksByCountry]);

  const unknownLabel = t("worldmap.unknownCountry");

  const tooltip = hover ?? (selectedInfo ? { ...selectedInfo, x: -1, y: -1 } : null);

  return (
    <div className="relative w-full h-full min-h-[420px] rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={resetZoom}
        className="absolute z-10 top-3 right-3 rounded-lg bg-white/95 border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 active:scale-95 transition"
        aria-label={t("worldmap.resetZoom")}
      >
        {t("worldmap.resetButton")}
      </button>

      {loadError && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm px-6 text-center">
          {loadError}
        </div>
      )}

      {!features && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
          {t("worldmap.loadingMap")}
        </div>
      )}

      {features && (
        <ComposableMap
          projectionConfig={{ scale: 148 }}
          className="w-full h-full"
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup
            center={position.coordinates}
            zoom={position.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={(pos) => setPosition({ coordinates: pos.coordinates, zoom: pos.zoom })}
          >
            <Geographies geography={features}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const countryNumeric = featureIdToCountryNumeric(geo.id);
                  const country = countryNumeric !== null ? countries.get(countryNumeric) : undefined;
                  const matched = Boolean(country);
                  const clicks = matched ? clicksByCountry.get(countryNumeric!)?.clicks ?? 0 : 0;
                  const isSelected = matched && countryNumeric === selectedCountryNumeric;
                  const name = matched
                    ? countryDisplayName(country, country!.name_en)
                    : (geo.properties?.name as string | undefined) ?? unknownLabel;

                  const fill = matched ? colorForClicks(clicks, maxClicks) : UNMATCHED_FEATURE_COLOR;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      tabIndex={matched ? 0 : -1}
                      role={matched ? "button" : undefined}
                      aria-label={matched ? t("worldmap.ariaClicks", { name, clicks: formatClicks(clicks, language) }) : undefined}
                      onMouseEnter={(evt) => {
                        if (!matched) return;
                        setHover({ name, clicks, x: evt.clientX, y: evt.clientY });
                      }}
                      onMouseMove={(evt) => {
                        if (!matched) return;
                        setHover({ name, clicks, x: evt.clientX, y: evt.clientY });
                      }}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => {
                        if (!matched || countryNumeric === null) return;
                        onSelectCountry(countryNumeric, name);
                      }}
                      onKeyDown={(evt) => {
                        if (!matched || countryNumeric === null) return;
                        if (evt.key === "Enter" || evt.key === " ") {
                          evt.preventDefault();
                          onSelectCountry(countryNumeric, name);
                        }
                      }}
                      style={{
                        default: {
                          fill,
                          stroke: matched ? BORDER_COLOR : UNMATCHED_STROKE,
                          strokeWidth: isSelected ? 1.75 : 0.5,
                          outline: "none",
                          cursor: matched ? "pointer" : "default",
                        },
                        hover: {
                          fill,
                          stroke: matched ? HOVER_STROKE : UNMATCHED_STROKE,
                          strokeWidth: matched ? 1.25 : 0.5,
                          outline: "none",
                          cursor: matched ? "pointer" : "default",
                        },
                        pressed: {
                          fill,
                          stroke: SELECTED_STROKE,
                          strokeWidth: 1.75,
                          outline: "none",
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      )}

      {tooltip && (
        <div
          className="pointer-events-none fixed z-20 rounded-lg bg-slate-900 text-white text-sm px-3 py-1.5 shadow-lg"
          style={
            tooltip.x >= 0
              ? { left: tooltip.x + 14, top: tooltip.y + 14 }
              : { left: 12, bottom: 12, position: "absolute" }
          }
        >
          <span className="font-semibold">{tooltip.name}</span>
          <span className="text-slate-300"> · {t("worldmap.tooltipClicks", { clicks: formatClicks(tooltip.clicks, language) })}</span>
        </div>
      )}
    </div>
  );
}
